import React, { useState } from 'react';
import {
  Card,
  Button,
  Table,
  Space,
  Tag,
  Input,
  Modal,
  Form,
  Select,
  Typography,
  Row,
  Col,
  Divider,
  App,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  useInspectionPointLibrary,
  useCreateInspectionPoint,
  useUpdateInspectionPoint,
  useDeleteInspectionPoint,
} from '../../hooks/useInspectionPointLibrary';
import type { InspectionPointLibrary } from '../../services/inspectionPointLibrary.service';

const { Title, Text } = Typography;
const { Search } = Input;
const { TextArea } = Input;
const { Option } = Select;

const InspectionPointLibraryPage: React.FC = () => {
  const { modal, message } = App.useApp();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPoint, setEditingPoint] = useState<InspectionPointLibrary | null>(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(undefined);

  const { data: inspectionPoints = [], isLoading } = useInspectionPointLibrary({
    search: searchText || undefined,
    category: categoryFilter || undefined,
    is_active: statusFilter,
  });

  const createPoint = useCreateInspectionPoint();
  const updatePoint = useUpdateInspectionPoint();
  const deletePoint = useDeleteInspectionPoint();

  const handleAdd = () => {
    setEditingPoint(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record: InspectionPointLibrary) => {
    setEditingPoint(record);
    form.setFieldsValue(record);
    setIsModalVisible(true);
  };

  const handleDelete = (record: InspectionPointLibrary) => {
    modal.confirm({
      title: 'Delete Inspection Point',
      content: `Are you sure you want to delete "${record.name}"?`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deletePoint.mutateAsync(record.id);
        } catch (error) {
          console.error('Error deleting inspection point:', error);
        }
      },
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingPoint) {
        await updatePoint.mutateAsync({
          id: editingPoint.id,
          data: values,
        });
      } else {
        await createPoint.mutateAsync(values);
      }
      setIsModalVisible(false);
      form.resetFields();
      setEditingPoint(null);
    } catch (error) {
      console.error('Error saving inspection point:', error);
    }
  };

  const categories = Array.from(new Set(inspectionPoints.map((p) => p.category))).filter(Boolean);

  const columns: ColumnsType<InspectionPointLibrary> = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 120,
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      width: 150,
      render: (category: string) => <Tag color="blue">{category}</Tag>,
      filters: categories.map((cat) => ({ text: cat, value: cat })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Type',
      dataIndex: 'inspection_type',
      width: 150,
      render: (type: string) => type && <Tag>{type}</Tag>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value, record) => record.is_active === value,
    },
    {
      title: 'Standard Ref',
      dataIndex: 'standard_reference',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2}>Inspection Point Library</Title>
          <Text type="secondary">
            Manage reusable inspection points for quality plans
          </Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Inspection Point
          </Button>
        </Col>
      </Row>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Search
              placeholder="Search by code, name, or description"
              allowClear
              onSearch={setSearchText}
              enterButton={<SearchOutlined />}
            />
          </Col>
          <Col span={8}>
            <Select
              placeholder="Filter by category"
              allowClear
              style={{ width: '100%' }}
              onChange={setCategoryFilter}
            >
              {categories.map((cat) => (
                <Option key={cat} value={cat}>
                  {cat}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: '100%' }}
              onChange={setStatusFilter}
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={inspectionPoints}
          rowKey="id"
          loading={isLoading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} inspection points`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      <Modal
        title={editingPoint ? 'Edit Inspection Point' : 'Add Inspection Point'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setEditingPoint(null);
        }}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Code"
                rules={[{ required: true, message: 'Please enter a code' }]}
              >
                <Input placeholder="e.g., IP-001" disabled={!!editingPoint} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select placeholder="Select category">
                  <Option value="Material">Material</Option>
                  <Option value="Process">Process</Option>
                  <Option value="Final">Final</Option>
                  <Option value="Dimensional">Dimensional</Option>
                  <Option value="Visual">Visual</Option>
                  <Option value="Weld">Weld</Option>
                  <Option value="NDT">NDT</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g., Dimensional Inspection" />
          </Form.Item>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Enter detailed description" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="inspection_type" label="Inspection Type">
                <Select placeholder="Select type" allowClear>
                  <Option value="Visual">Visual</Option>
                  <Option value="Dimensional">Dimensional</Option>
                  <Option value="Functional">Functional</Option>
                  <Option value="NDT">NDT</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Status" initialValue={true}>
                <Select>
                  <Option value={true}>Active</Option>
                  <Option value={false}>Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="standard_reference" label="Standard Reference">
            <Input placeholder="e.g., ASME B31.3, API 650" />
          </Form.Item>

          <Form.Item name="required_qualification" label="Required Qualification">
            <Input placeholder="e.g., Level II NDT Inspector" />
          </Form.Item>

          <Form.Item name="documentation_requirements" label="Documentation Requirements">
            <TextArea rows={2} placeholder="Required documentation for this inspection" />
          </Form.Item>

          <Divider />

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createPoint.isPending || updatePoint.isPending}
              >
                {editingPoint ? 'Update' : 'Create'}
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                  setEditingPoint(null);
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default InspectionPointLibraryPage;
